import type { ReactNode } from "react";

import type { Blok, Ontwerp, Rij, ToonAls } from "./ontwerpTypes";
import "./schermbouwer.css";

/**
 * Tekent een ontwerp.
 *
 * <p>Deze component weet niets van medicatie, en dat is de hele bedoeling. Hij kent bouwblokken,
 * velden en voorwaarden -- wat die velden betekenen staat in de catalogus en in de gegevens die hij
 * binnenkrijgt. Zou hier ook maar één regel medicatiekennis landen, dan is de volgende tegelsoort
 * geen catalogusuitbreiding meer maar een codewijziging, en dan is het plan mislukt.
 *
 * <p>Een blok dat hij niet kent tekent hij als zichtbare melding in plaats van het weg te laten. Een
 * ontwerp dat de server goedkeurt maar dat hier stilletjes half verdwijnt, is het vervelendste soort
 * fout: de gebruiker ziet iets kloppends en mist wat er niet staat.
 */

/** De voorwaarden uit de catalogus, hier uitgerekend voor het voorbeeld. */
function voldoet(voorwaarde: ToonAls | undefined, rij: Rij, aggregaten: Record<string, number>): boolean {
  if (!voorwaarde) return true;
  if ("alle" in voorwaarde) return voorwaarde.alle.every((v) => voldoet(v, rij, aggregaten));
  if ("een_van" in voorwaarde) return voorwaarde.een_van.some((v) => voldoet(v, rij, aggregaten));

  // Zonder veld gaat de voorwaarde over een aggregatie; die staat niet op een rij.
  const waarde = voorwaarde.veld ? rij[voorwaarde.veld] : aggregaten.huidige;

  switch (voorwaarde.conditie) {
    case "is_leeg":
      return waarde === null || waarde === undefined || waarde === "";
    case "is_gelijk_aan":
      return waarde === voorwaarde.waarde;
    case "groter_dan":
      return typeof waarde === "number" && waarde > Number(voorwaarde.waarde ?? 0);
    case "niet_afgevinkt":
      return waarde !== true;
    case "tijdstip_verstreken":
      return tijdstipVerstreken(waarde);
    default:
      // Een conditie die het scherm niet kent is door de server goedgekeurd, dus hij bestaat --
      // alleen kan deze renderer hem nog niet uitrekenen. Tonen is dan veiliger dan verbergen.
      return true;
  }
}

/** "14:30" of een ISO-tijd vergeleken met nu; alles wat niet te lezen is telt als niet verstreken. */
function tijdstipVerstreken(waarde: unknown): boolean {
  if (typeof waarde !== "string" || waarde.trim() === "") return false;
  const nu = new Date();
  const klok = /^(\d{1,2}):(\d{2})/.exec(waarde);
  if (klok) {
    const moment = new Date(nu);
    moment.setHours(Number(klok[1]), Number(klok[2]), 0, 0);
    return moment.getTime() < nu.getTime();
  }
  const gelezen = new Date(waarde);
  return !Number.isNaN(gelezen.getTime()) && gelezen.getTime() < nu.getTime();
}

function toon(waarde: unknown, formaat?: string): string {
  if (waarde === null || waarde === undefined || waarde === "") return "—";
  if (typeof waarde === "boolean") return waarde ? "Ja" : "Nee";
  if (formaat === "HH:mm" && typeof waarde === "string") {
    const klok = /^(\d{1,2}):(\d{2})/.exec(waarde);
    if (klok) return `${klok[1].padStart(2, "0")}:${klok[2]}`;
  }
  return String(waarde);
}

function kleurklasse(kleur?: string): string {
  return kleur ? ` sb-kleur--${kleur}` : "";
}

function grootteklasse(grootte?: string): string {
  return grootte ? ` sb-grootte--${grootte}` : "";
}

/**
 * Wat deze kijker mag zien.
 *
 * <p>Harde regel 7: een gepubliceerde tegel toont aan elke gebruiker alleen de velden en acties die
 * díe gebruiker mag, ongeacht de rechten van de ontwerper. In het voorbeeld is dit de bril van
 * "bekijk als rol" -- en het is echt een bril: hij haalt weg, hij voegt nooit iets toe. Daarom mag
 * iedereen hem opzetten zonder dat het iets ontsluit.
 *
 * <p>Niets meegeven betekent "alles wat in het ontwerp staat", want dan heeft de server het al
 * gefilterd voordat het hier kwam.
 */
export interface Bril {
  velden: Set<string>;
  acties: Set<string>;
}

function zichtbaar(blok: Blok, bril?: Bril): boolean {
  if (!bril) return true;
  if (blok.veld && !bril.velden.has(blok.veld)) return false;
  if (blok.actie && !bril.acties.has(blok.actie)) return false;
  return true;
}

interface TekenProps {
  blok: Blok;
  rij: Rij;
  aggregaten: Record<string, number>;
  bril?: Bril;
  /** Wat er gebeurt bij een klik op een actieknop; in het voorbeeld doet die niets. */
  opActie?: (actie: string, rij: Rij) => void;
  /** In het voorbeeld is een knop zichtbaar maar niet bruikbaar. */
  alleenTonen?: boolean;
  pad: string;
}

export function TekenBlok({ blok, rij, aggregaten, bril, opActie, alleenTonen, pad }: TekenProps): ReactNode {
  if (!zichtbaar(blok, bril)) return null;
  if (!voldoet(blok.toonAls, rij, aggregaten)) return null;

  const kinderen = (blok.onderdelen ?? []).map((kind, i) => (
    <TekenBlok
      key={`${pad}.${i}`}
      blok={kind}
      rij={rij}
      aggregaten={aggregaten}
      bril={bril}
      opActie={opActie}
      alleenTonen={alleenTonen}
      pad={`${pad}.${i}`}
    />
  ));

  switch (blok.blok) {
    case "tekst":
      return (
        <span className={`sb-tekst${grootteklasse(blok.grootte)}${kleurklasse(blok.stijl?.kleur)}${blok.nadruk ? " sb-tekst--nadruk" : ""}`}>
          {blok.veld ? toon(rij[blok.veld], blok.formaat) : blok.tekst ?? ""}
        </span>
      );

    case "afbeelding": {
      const bron = blok.veld ? rij[blok.veld] : null;
      return typeof bron === "string" && bron !== "" ? (
        <img className={`sb-foto${grootteklasse(blok.grootte)}`} src={bron} alt="" />
      ) : (
        <span className={`sb-foto sb-foto--leeg${grootteklasse(blok.grootte)}`} aria-hidden="true" />
      );
    }

    case "badge":
      return (
        <span className={`sb-badge${kleurklasse(blok.stijl?.kleur)}`}>
          {blok.aggregatie ? aggregaten[blok.aggregatie] ?? 0 : toon(blok.veld ? rij[blok.veld] : blok.tekst)}
          {blok.tekst && blok.aggregatie ? ` ${blok.tekst}` : ""}
        </span>
      );

    case "waarschuwing":
      return (
        <span className={`sb-waarschuwing${kleurklasse(blok.stijl?.kleur)}`} role="status">
          {blok.tekst ?? (blok.veld ? toon(rij[blok.veld]) : "")}
        </span>
      );

    case "knop":
      return (
        <button
          type="button"
          className="sb-knop"
          disabled={alleenTonen}
          onClick={() => blok.actie && opActie?.(blok.actie, rij)}
        >
          {blok.tekst ?? blok.actie ?? ""}
        </button>
      );

    case "lijstregel":
      return <div className="sb-lijstregel">{kinderen}</div>;

    case "lijst":
      return <div className="sb-lijst">{kinderen}</div>;

    case "tabel":
      return <div className="sb-tabel">{kinderen}</div>;

    case "zorgschema":
      // Het zorgschema zelf wordt door Docasoft getekend; hier staat waar het komt en in welke
      // weergave, zodat de ontwerper de ruimte ziet die het inneemt.
      return (
        <div className="sb-zorgschema">
          <span className="sb-zorgschema__naam">{blok.zorgschema}</span>
          {blok.weergave && <span className="sb-zorgschema__weergave">{blok.weergave}</span>}
        </div>
      );

    default:
      return <span className="sb-onbekend">Onbekend blok: {blok.blok}</span>;
  }
}

/**
 * Het hele ontwerp met zijn gegevens.
 *
 * <p>De kop staat er één keer, de regel per rij. Dat is de vorm die het plan beschrijft en meteen de
 * reden dat een tegel met echte gegevens nooit een kopie van die gegevens bewaart: het recept staat
 * vast, de inhoud komt er elke keer opnieuw bij.
 */
export function OntwerpRenderer({
  ontwerp,
  rijen,
  aggregaten = {},
  bril,
  opActie,
  alleenTonen = true,
}: {
  ontwerp: Ontwerp;
  rijen: Rij[];
  aggregaten?: Record<string, number>;
  bril?: Bril;
  opActie?: (actie: string, rij: Rij) => void;
  alleenTonen?: boolean;
}) {
  const getoond = rijen.slice(0, ontwerp.bron?.maxRegels ?? rijen.length);

  return (
    <div className={`sb-tegel sb-tegel--${ontwerp.indeling ?? "ruim"}`}>
      <div className="sb-tegel__kop">
        <span className="sb-tegel__titel">{ontwerp.titel ?? "Tegel"}</span>
        {ontwerp.kop && (
          <TekenBlok
            blok={ontwerp.kop}
            rij={{}}
            aggregaten={{ ...aggregaten, huidige: aggregaten[ontwerp.kop.aggregatie ?? ""] ?? 0 }}
            bril={bril}
            opActie={opActie}
            alleenTonen={alleenTonen}
            pad="kop"
          />
        )}
      </div>

      {getoond.length === 0 ? (
        <p className="sb-leeg">Geen regels om te tonen.</p>
      ) : (
        <div className="sb-tegel__regels">
          {getoond.map((rij, i) => (
            <TekenBlok
              key={i}
              blok={ontwerp.regel}
              rij={rij}
              aggregaten={aggregaten}
              bril={bril}
              opActie={opActie}
              alleenTonen={alleenTonen}
              pad={`regel.${i}`}
            />
          ))}
        </div>
      )}
    </div>
  );
}
